import { brandStatusMeta } from '@/app/brands/brandReviewStatus';
import { HintTooltip } from '@/components/HintTooltip';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import type { Brand } from '@rock/types';
import { approvedBrandAssets } from '@rock/types';
import { X } from 'lucide-react';
import type { FC } from 'react';
import { Link } from 'react-router-dom';

/** The brand's curation status as a bare icon — the checkmark an approved brand earns — explained on hover. */
export const BrandReviewStatusIcon: FC<{ brand: Pick<Brand, 'reviewStatus' | 'mergedInto'>; className?: string }> = ({
    brand,
    className,
}) => {
    const meta = brandStatusMeta(brand);
    return (
        <HintTooltip
            className="shrink-0"
            hint={
                <>
                    <span className="font-medium">{meta.label}</span>
                    <span>{meta.hint}</span>
                </>
            }
        >
            <meta.icon className={cn('h-4 w-4', meta.iconClassName, className)} aria-label={meta.label} />
        </HintTooltip>
    );
};

/** The brand's curation status as a badge, explained on hover. */
export const BrandStatusBadge: FC<{ brand: Pick<Brand, 'reviewStatus' | 'mergedInto'>; className?: string }> = ({
    brand,
    className,
}) => {
    const meta = brandStatusMeta(brand);
    return (
        <HintTooltip hint={meta.hint}>
            <Badge variant={meta.variant} className={cn(meta.className, className)}>
                {meta.label}
            </Badge>
        </HintTooltip>
    );
};

/**
 * A catalog brand as one chip: its mark, its curation status icon and its name linking to its page. The
 * mark comes through `approvedBrandAssets`, the gate the app renders behind, so unvetted imagery on a
 * brand not yet approved stays hidden here too. `onRemove` turns it into a removable pill. The name's link keeps
 * its click to itself, so a chip inside a clickable table row opens the brand, not the row.
 */
export const BrandChip: FC<{ brand: Brand; onRemove?: () => void; className?: string }> = ({
    brand,
    onRemove,
    className,
}) => {
    const assets = approvedBrandAssets(brand);
    const mark = assets.icon ?? assets.logo;
    const name = brand.displayName ?? brand.canonicalName;
    return (
        <span
            className={cn(
                'inline-flex h-7 max-w-full items-center gap-1.5 rounded-full border bg-background pl-1.5 pr-2.5 text-sm',
                className,
            )}
        >
            {mark && <img src={mark.thumbnail} alt="" className="h-5 w-auto max-w-[56px] shrink-0 object-contain" />}
            <BrandReviewStatusIcon brand={brand} />
            <Link
                to={`/products/brands/${encodeURIComponent(brand.id)}`}
                onClick={(event) => event.stopPropagation()}
                className="truncate font-medium text-foreground underline-offset-2 hover:underline"
                title={brand.canonicalName}
            >
                {name}
            </Link>
            {onRemove && (
                <button
                    type="button"
                    onClick={onRemove}
                    className="text-muted-foreground hover:text-destructive"
                    aria-label={`Remove brand ${name}`}
                >
                    <X className="h-3 w-3" />
                </button>
            )}
        </span>
    );
};
